/*
 * @Author: lizhigang
 * @Date: 2025-10-22 17:44:55
 * @Company: www.smm.cn
 * @Description: 忘记密码
 */
"use client"

import IconFont from "@repo/ui/iconfont"
import { useLocale, useTranslations } from "next-intl"
import { AccountInput, composeCellphone, resolveAccountType, type AccountInputValue } from "smm-auth-widget"
import useVerificationCode from "smm-auth-widget/hooks/useVerificationCode"
import { useResetPassword } from "smm-auth-widget/ui"
import { useCallback, useEffect, useState, type ReactNode } from "react"
import { Button, Col, Form, Input, message, Row, Space } from "antd"

import "./index.scss"

import { usePasswordInput } from "../../_hooks"
import { SIGN_IN_EN_H5_SOURCE_MAP, SIGN_IN_EN_WEB_SOURCE_MAP } from "../../const"
import { isMobile } from "../../utils"
import { AuthConfigProvider } from "../AuthConfigProvider"
import { createPasswordValidator, PasswordRulesPopover } from "../PasswordRulesPopover"

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  onReseted?: (msg?: string) => void
}

interface VerificationCodeControlProps {
  value?: string
  onChange?: (value: string) => void
  isPhone: boolean
  placeholder: string
  sendButton: ReactNode
  switchLabel: string
  onPressEnter: () => void
  onToggleChannel: () => void
}

const VerificationCodeControl = ({
  value = "",
  onChange,
  isPhone,
  placeholder,
  sendButton,
  switchLabel,
  onPressEnter,
  onToggleChannel,
}: VerificationCodeControlProps) => (
  <>
    <Space.Compact block>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        onPressEnter={onPressEnter}
        allowClear
      />
      <span className="sendButton">{sendButton}</span>
    </Space.Compact>
    {isPhone && (
      <span className="verificationLabel">
        <button className="verifySwitchButton" type="button" onClick={onToggleChannel}>
          {switchLabel}
        </button>
      </span>
    )}
  </>
)

const ForgotPassword = ({ onReseted, ...attr }: Props) => {
  const locale = useLocale()
  const t = useTranslations()

  const [form] = Form.useForm()
  const [account, setAccount] = useState<AccountInputValue>({ account: "", phoneCode: "" })
  const [accountHasError, setAccountHasError] = useState(true)
  const [verifyChannel, setVerifyChannel] = useState<"sms" | "whatsapp">("sms")
  const [password, setPassword] = useState("")
  const [isH5, setIsH5] = useState(false)
  const [loading, resetPasswordApi] = useResetPassword()
  const [messageApi, contextHolder] = message.useMessage({
    getContainer: () => document.querySelector("#action") || document.body,
  })
  const verifyCode = Form.useWatch("verify_code", form) ?? ""

  const accountType = resolveAccountType(account.account, "auto")
  const cellphone = accountType === "phone" ? composeCellphone(account.phoneCode, account.account) : ""
  const userName = accountType === "email" ? account.account : cellphone
  const { sendButton, requestCode } = useVerificationCode({
    type: accountType,
    action: "forgot",
    locale,
    params: accountType === "email" ? { email: account.account } : { cellphone, channel: verifyChannel },
    requestKey: accountType === "email" ? `email:${account.account}` : `phone:${cellphone}:${verifyChannel}`,
    disabled: accountHasError || loading,
    showError: (content) => messageApi.open({ type: "error", content }),
  })
  const handleToggleVerifyChannel = useCallback(() => {
    setVerifyChannel((channel) => (channel === "sms" ? "whatsapp" : "sms"))
    form.setFieldValue("verify_code", "")
  }, [form])
  const verifyLabel = t(verifyChannel === "sms" ? "signInCommon.verifyViaSms" : "signInCommon.verifyViaWhatsapp")
  const verifySwitchLabel = t(verifyChannel === "sms" ? "signInCommon.verifyViaWhatsapp" : "signInCommon.verifyViaSms")

  // 使用密码输入 Hook - 新密码
  const { handlePasswordChange, handlePasswordPaste, handleCompositionStart } = usePasswordInput({
    form,
    fieldName: "password",
    onPasswordChange: setPassword,
  })

  // 使用密码输入 Hook - 确认密码
  const {
    handlePasswordChange: handlePassword2Change,
    handlePasswordPaste: handlePassword2Paste,
    handleCompositionStart: handlePassword2CompositionStart,
  } = usePasswordInput({
    form,
    fieldName: "password2",
  })

  useEffect(() => {
    setIsH5(isMobile())
  }, [])

  /**
   * 重置密码
   */
  const handleSubmit = async () => {
    if (loading || accountHasError || !password.trim() || !verifyCode.trim()) return
    const values = await form.validateFields()
    if (!values) return

    await resetPasswordApi({
      values: {
        email: userName,
        password: values.password,
        verifyCode,
        locale,
        source: isMobile()
          ? (SIGN_IN_EN_H5_SOURCE_MAP[window.location.host] ?? "")
          : (SIGN_IN_EN_WEB_SOURCE_MAP[window.location.host] ?? ""),
      },
      onSuccess: () => {
        messageApi.open({
          type: "success",
          content: t("forgot.resetSuccess"),
        })
        form.resetFields()
        onReseted?.(t("forgot.resetSuccess"))
      },
      onError: (msg) => {
        messageApi.open({
          type: "error",
          content: msg || "unknown error",
        })
      },
    })
  }

  return (
    <AuthConfigProvider>
      {contextHolder}
      <div className="forgotWrapper page" {...attr}>
        <div className="logo">
          <IconFont type="icon-SMMlogo-daizimu" style={{ width: 80, height: 32, fontSize: 80 }} />
        </div>
        <h2 className="modalTitle page">{t("forgot.title")}</h2>
        {/*<ConfigProvider*/}
        {/*  theme={{*/}
        {/*    components: {*/}
        {/*      Form: {*/}
        {/*        verticalLabelMargin: 40,*/}
        {/*      },*/}
        {/*    },*/}
        {/*  }}*/}
        {/*>*/}
        <Form
          layout="vertical"
          form={form}
          variant="filled"
          initialValues={{ variant: "borderless" }}
          className={"forgotForm"}
          requiredMark={false}
        >
          <div className="accountInputWrapperClass">
            <Form.Item label={null}>
              <AccountInput
                mode="auto"
                locale={locale}
                value={account}
                className="forgotAccountInput"
                placeholder={t("accountInput.accountPlaceholder")}
                onChange={(value, hasError) => {
                  if (resolveAccountType(value.account, "auto") !== accountType) setVerifyChannel("sms")
                  setAccount(value)
                  setAccountHasError(hasError)
                }}
                onEnter={() => void requestCode()}
              />
            </Form.Item>
          </div>
          <Form.Item
            name="verify_code"
            className={accountType === "phone" ? "verificationCodeItem" : undefined}
            label={null}
            normalize={(value) => value?.trim()}
            rules={[{ required: true, message: t("signInCommon.requireCode") }]}
          >
            <VerificationCodeControl
              isPhone={accountType === "phone"}
              placeholder={accountType === "email" ? t("forgot.emailCode") : verifyLabel}
              sendButton={sendButton}
              switchLabel={verifySwitchLabel}
              onPressEnter={handleSubmit}
              onToggleChannel={handleToggleVerifyChannel}
            />
          </Form.Item>
          <Row>
            <Col className="passwordWrapper">
              <Form.Item
                name="password"
                className="password"
                label={null}
                normalize={(value) => value?.trim()}
                rules={[
                  { required: true, message: t("forgot.requirePassword") },
                  { validator: createPasswordValidator(t) },
                ]}
              >
                <PasswordRulesPopover>
                  <Input.Password
                    placeholder={t("forgot.newPassword")}
                    onChange={handlePasswordChange}
                    onPressEnter={handleSubmit}
                    onCompositionStart={handleCompositionStart}
                    onPaste={handlePasswordPaste}
                    allowClear
                  />
                </PasswordRulesPopover>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="password2"
            className="password2"
            style={{ marginBottom: isH5 ? 20 : undefined }}
            dependencies={["password"]}
            label={null}
            normalize={(value) => value?.trim()}
            rules={[
              { required: true, message: t("forgot.requireConfirmPassword") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error(t("forgot.newPassword_error2")))
                },
              }),
            ]}
          >
            <Input.Password
              placeholder={t("forgot.confirmPassword")}
              onChange={handlePassword2Change}
              onPressEnter={handleSubmit}
              onCompositionStart={handlePassword2CompositionStart}
              onPaste={handlePassword2Paste}
              allowClear
            />
          </Form.Item>
          {isH5 && <div className="simplePasswordTip">{t("forgot.simplePasswordTip")}</div>}
          <Form.Item className="submitButton">
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={accountHasError || !password.trim() || !verifyCode.trim()}
              loading={loading}
              block
              danger
            >
              {t("forgot.reset")}
            </Button>
          </Form.Item>
        </Form>
        {/*</ConfigProvider>*/}
      </div>
    </AuthConfigProvider>
  )
}

export default ForgotPassword
